import React, {useMemo, useState} from "react";
import {createPortal} from "react-dom";
import {
    BadgeCheck, LayoutGrid, List, PackageSearch, Plus, ShieldCheck, ShoppingBag, ShoppingCart, Sparkles,
} from "lucide-react";
import Button from "../Common/Button";
import {
    ListEmpty, ListLoader, Modal, Panel, SearchInput, mobileListClass, mobileRowClass, thClass,
} from "../../stock/StockUI";
import {formatMoney, hasDiscount} from "./marketplaceApi";
import {
    CategoryChips, DiscountBadge, PriceCell, PriceTag, ProductCover, ProductThumb, StockBadge, StockDot, ViewToggle, iconFor,
} from "./MarketplaceUI";

const VIEW_OPTIONS = [
    {key: "grid", label: "Grille", Icon: LayoutGrid},
    {key: "list", label: "Liste", Icon: List},
];
const VIEW_STORAGE_KEY = "marketplaceView";

const readView = () => {
    try {
        return localStorage.getItem(VIEW_STORAGE_KEY) === "list" ? "list" : "grid";
    } catch {
        return "grid";
    }
};

// ─── Bandeau ─────────────────────────────────────────────────────────────────

const PERKS = [
    {Icon: BadgeCheck, label: "Compatible QuickCom"},
    {Icon: ShieldCheck, label: "Garantie incluse"},
    {Icon: Sparkles, label: "Remises exclusives clients"},
];

const IntroBanner = ({deals}) => (
    <div className="border-b border-gray-200 dark:border-gray-700 bg-primary-dark">
        <div className="px-4 sm:px-8 py-6 sm:py-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-white">Équipez votre point de vente</h2>
                <p className="mt-1 text-sm text-white/80 max-w-xl">
                    Caisses, imprimantes, tablettes, supports et consommables sélectionnés pour votre caisse QuickCom.
                    {deals > 0 && <> <span className="font-semibold text-white">{deals} produits en promotion</span> en ce moment.</>}
                </p>
            </div>
            <div className="flex flex-wrap gap-2">
                {PERKS.map(({Icon, label}) => (
                    <span key={label}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 text-white text-xs sm:text-sm">
                        <Icon className="w-4 h-4" strokeWidth={1.75}/> {label}
                    </span>
                ))}
            </div>
        </div>
    </div>
);

// ─── Produit ─────────────────────────────────────────────────────────────────

// Cart button: "add" when the product is not in the cart, its quantity otherwise
const CartButton = ({product, inCart, onAdd, compact = false}) => inCart ? (
    <Button variant="tinted" size="sm" icon={Plus} title="Ajouter un de plus" onClick={() => onAdd(product)}>
        <span className="tabular-nums">{inCart}</span>{!compact && <span className="hidden xl:inline">dans le panier</span>}
    </Button>
) : (
    <Button variant="tinted" size="sm" icon={ShoppingCart} title="Ajouter au panier" onClick={() => onAdd(product)}>
        {!compact && <span className="hidden xl:inline">Ajouter</span>}
    </Button>
);

// Two row actions: add to the cart, or order right away.
const ProductActions = ({product, inCart, onAdd, onOrder}) => (
    <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
        <CartButton product={product} inCart={inCart} onAdd={onAdd}/>
        <Button variant="primary" size="sm" icon={ShoppingBag} onClick={() => onOrder(product)}>
            Commander
        </Button>
    </div>
);

// Grid card: cover, name, description, price, then the two actions
const ProductCard = ({product, inCart, onOpen, onAdd, onOrder}) => (
    <div onClick={() => onOpen(product)}
         className={`group relative flex flex-col bg-white dark:bg-gray-800 rounded-xl overflow-hidden border transition-all duration-200 cursor-pointer hover:-translate-y-0.5 hover:shadow-lg ${
             inCart ? "border-primary/60 shadow-sm shadow-primary/10" : "border-gray-200 dark:border-gray-700 shadow-sm hover:border-primary/40"
         }`}>
        <div className="absolute top-3 left-3 z-10"><DiscountBadge product={product}/></div>
        {inCart > 0 && (
            <span className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary text-white text-xs font-semibold shadow-sm">
                <ShoppingCart className="w-3 h-3"/> {inCart}
            </span>
        )}
        <ProductCover product={product}/>
        <div className="flex flex-col flex-1 p-4 gap-1.5">
            <StockDot inStock={product.inStock}/>
            <h3 className="text-[15px] font-semibold leading-snug text-gray-900 dark:text-gray-100 group-hover:text-primary-dark dark:group-hover:text-primary transition-colors">
                {product.name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{product.description}</p>
            <div className="mt-auto pt-3"><PriceTag product={product}/></div>
        </div>
        <div className="flex gap-2 px-4 pb-4" onClick={(e) => e.stopPropagation()}>
            <CartButton product={product} inCart={inCart} onAdd={onAdd} compact/>
            <Button variant="primary" size="sm" icon={ShoppingBag} className="flex-1" onClick={() => onOrder(product)}>
                Commander
            </Button>
        </div>
    </div>
);

const ProductDetailsModal = ({product, categoryLabel, inCart, onClose, onAdd, onOrder}) => createPortal(
    <Modal
        title={product.name}
        subtitle={categoryLabel}
        onClose={onClose}
        size="lg"
        footer={
            <div className="grid grid-cols-2 border-t border-gray-200 dark:border-gray-700">
                <button type="button" onClick={() => onAdd(product)}
                        className="py-4 flex items-center justify-center gap-2 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <ShoppingCart className="w-4 h-4"/> Ajouter au panier{inCart > 0 && ` (${inCart})`}
                </button>
                <button type="button" onClick={() => { onClose(); onOrder(product); }}
                        className="py-4 flex items-center justify-center gap-2 font-medium text-primary border-l border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <ShoppingBag className="w-4 h-4"/> Commander
                </button>
            </div>
        }
    >
        <div className="space-y-5">
            <div className="relative rounded-xl overflow-hidden">
                <div className="absolute top-3 left-3 z-10"><DiscountBadge product={product}/></div>
                <ProductCover product={product} className="h-48"/>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <PriceTag product={product} large/>
                <div className="flex items-center gap-3">
                    <StockBadge inStock={product.inStock}/>
                    {product.warranty && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
                            <ShieldCheck className="w-4 h-4 text-primary"/> Garantie {product.warranty}
                        </span>
                    )}
                </div>
            </div>

            <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">{product.description}</p>

            {product.features?.length > 0 && (
                <div className="rounded-xl bg-gray-50 dark:bg-gray-700/40 p-4">
                    <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider mb-3">
                        Caractéristiques
                    </div>
                    <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-700 dark:text-gray-300">
                        {product.features.map((feature) => (
                            <li key={feature} className="flex gap-2">
                                <BadgeCheck className="w-4 h-4 mt-0.5 text-primary flex-shrink-0"/>
                                {feature}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    </Modal>,
    document.body
);

// ─── Onglet ──────────────────────────────────────────────────────────────────

const SectionTitle = ({section}) => {
    const Icon = iconFor(section.key);
    return (
        <div className="flex items-center gap-3 mb-4">
            <div className="h-9 w-9 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                <Icon className="w-5 h-5 text-primary-dark dark:text-primary" strokeWidth={1.75}/>
            </div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">{section.label}</h2>
            <span className="text-sm text-gray-400">{section.products.length}</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"/>
        </div>
    );
};

// Sticky reminder of the cart at the bottom of the catalogue
const CartBar = ({cart, onViewCart, onCheckout}) => (
    <div className="sticky bottom-0 z-20 border-t border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md">
        <div className="px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
                <div className="relative h-10 w-10 flex-shrink-0 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                    <ShoppingCart className="w-5 h-5 text-primary"/>
                    <span className="absolute -top-1 -right-1 h-5 min-w-[1.25rem] px-1 rounded-full bg-primary text-white text-xs font-semibold flex items-center justify-center">
                        {cart.count}
                    </span>
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatMoney(cart.total)}</div>
                    {cart.discount > 0 && (
                        <div className="text-xs text-red-600 dark:text-red-400 truncate">-{formatMoney(cart.discount)} de remises</div>
                    )}
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">livraison {formatMoney(cart.deliveryFee)} incluse</div>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={onViewCart} className="hidden sm:inline-flex">Voir le panier</Button>
                <Button variant="primary" size="sm" icon={ShoppingBag} onClick={onCheckout}>Commander</Button>
            </div>
        </div>
    </div>
);

const MarketplaceCatalogTab = ({categories, products, isLoading, isError, cart, onAdd, onOrder, onViewCart, onCheckout}) => {
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("");
    const [selected, setSelected] = useState(null);
    const [view, setView] = useState(readView);

    const changeView = (next) => {
        setView(next);
        try {
            localStorage.setItem(VIEW_STORAGE_KEY, next);
        } catch {
            // storage unavailable: the choice just lasts for this visit
        }
    };

    const counts = useMemo(() => products.reduce((acc, p) => ({...acc, [p.category]: (acc[p.category] || 0) + 1}), {}), [products]);
    const deals = products.filter(hasDiscount).length;
    const quantityOf = (id) => cart.lines.find((l) => l.product.id === id)?.quantity || 0;

    // Products matching the filters, grouped by category (catalogue order).
    const sections = useMemo(() => {
        const term = search.trim().toLowerCase();
        const matches = products.filter((p) => !term || `${p.name} ${p.description}`.toLowerCase().includes(term));
        return categories
            .filter((c) => !category || c.key === category)
            .map((c) => ({...c, products: matches.filter((p) => p.category === c.key)}))
            .filter((c) => c.products.length > 0);
    }, [categories, products, category, search]);

    const labelFor = (key) => categories.find((c) => c.key === key)?.label || "";

    return (
        <div className="max-w-full">
            <IntroBanner deals={deals}/>

            <div className="flex flex-col sm:flex-row">
                <SearchInput value={search} onChange={setSearch} placeholder="Rechercher une caisse, une imprimante, une tablette..."/>
                <ViewToggle value={view} onChange={changeView} options={VIEW_OPTIONS}/>
            </div>
            <CategoryChips categories={categories} value={category} onChange={setCategory} counts={counts} total={products.length}/>

            {isLoading ? (
                <ListLoader/>
            ) : isError ? (
                <p className="text-center text-red-500 py-12">Impossible de charger le catalogue.</p>
            ) : sections.length === 0 ? (
                <ListEmpty icon={PackageSearch} title="Aucun produit trouvé" text="Modifiez la recherche ou la catégorie."/>
            ) : view === "grid" ? (
                <div className="px-4 sm:px-8 py-6 space-y-10 bg-gray-50/70 dark:bg-gray-900">
                    {sections.map((section) => (
                        <section key={section.key}>
                            <SectionTitle section={section}/>
                            <div className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                                {section.products.map((product) => (
                                    <ProductCard key={product.id} product={product} inCart={quantityOf(product.id)}
                                                 onOpen={setSelected} onAdd={onAdd} onOrder={onOrder}/>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            ) : (
                <div className="space-y-4 sm:space-y-6 py-4">
                    {sections.map((section) => (
                        <Panel key={section.key} title={section.label}
                               aside={<span className="text-xs text-gray-500 dark:text-gray-400">{section.products.length} produit(s)</span>}>
                            {/* Desktop table */}
                            <div className="hidden lg:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                                    <tr>
                                        <th className={thClass}>Produit</th>
                                        <th className={`${thClass} text-right`}>Prix</th>
                                        <th className={thClass}>Remise</th>
                                        <th className={thClass}>Disponibilité</th>
                                        <th className={thClass}><span className="sr-only">Actions</span></th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {section.products.map((product) => (
                                        <tr key={product.id} onClick={() => setSelected(product)}
                                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-4">
                                                    <ProductThumb product={product}/>
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{product.name}</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 max-w-md">{product.description}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right"><PriceCell product={product}/></td>
                                            <td className="px-6 py-4">{hasDiscount(product) ? <DiscountBadge product={product}/> : <span className="text-gray-400">—</span>}</td>
                                            <td className="px-6 py-4 whitespace-nowrap"><StockBadge inStock={product.inStock}/></td>
                                            <td className="px-6 py-4">
                                                <ProductActions product={product} inCart={quantityOf(product.id)} onAdd={onAdd} onOrder={onOrder}/>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile rows */}
                            <div className={mobileListClass}>
                                {section.products.map((product) => (
                                    <div key={product.id} onClick={() => setSelected(product)} className={`${mobileRowClass} cursor-pointer`}>
                                        <div className="flex items-start gap-3">
                                            <ProductThumb product={product}/>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{product.name}</div>
                                                    <DiscountBadge product={product}/>
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">{product.description}</div>
                                                <div className="flex items-center justify-between gap-2 mt-2">
                                                    <PriceCell product={product}/>
                                                    <StockDot inStock={product.inStock}/>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
                                            <CartButton product={product} inCart={quantityOf(product.id)} onAdd={onAdd} compact/>
                                            <Button variant="primary" size="sm" icon={ShoppingBag} className="flex-1" onClick={() => onOrder(product)}>
                                                Commander
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Panel>
                    ))}
                </div>
            )}

            {cart.count > 0 && <CartBar cart={cart} onViewCart={onViewCart} onCheckout={onCheckout}/>}

            {selected && (
                <ProductDetailsModal
                    product={selected}
                    categoryLabel={labelFor(selected.category)}
                    inCart={quantityOf(selected.id)}
                    onClose={() => setSelected(null)}
                    onAdd={onAdd}
                    onOrder={onOrder}
                />
            )}
        </div>
    );
};

export default MarketplaceCatalogTab;
